import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, Popover } from '@leanin/ui';
import type { ReactionKind } from '@leanin/shared/reactions';
import {
  DEFAULT_REACTION,
  REACTION_BY_ID,
  REACTION_LABEL_KEY,
  ReactionDisc,
  ReactionPicker,
  OUTLINE_HEART,
} from './ReactionPicker';

// Touch devices can't hover the button open — a long-press (pointerdown held
// this long) opens the picker instead.
const LONG_PRESS_MS = 450;
// Grace period for the pointer to travel from the button into the picker.
const PICKER_CLOSE_DELAY_MS = 160;

type Props = {
  /** The viewer's current reaction. */
  mine: ReactionKind | null;
  /** Applies `kind`; picking the held kind again removes it. */
  onReact: (kind: ReactionKind) => void;
  /** `chip` sits in a post's or article's action row; `text` sits in a
   *  comment's Like · Reply · time row. */
  variant: 'chip' | 'text';
  /** What the reaction is on — names the button for assistive tech and
   *  tests, since every surface's visible label is the same "Like". */
  target: 'post' | 'comment' | 'article';
  disabled?: boolean;
  /** Line the button's icon up with the content edge (Button `flush`). */
  flush?: 'start' | 'end';
};

// The viewer's reaction control for posts, comments and articles: the held
// reaction's icon and name (or an outline heart and "Like"). A click
// toggles; hover, a touch long-press, or ArrowUp/ArrowDown on the focused
// button opens the picker for the other kinds. Who reacted lives in
// ReactionSummary.
export function ReactionButton({
  mine,
  onReact,
  variant,
  target,
  disabled = false,
  flush,
}: Props) {
  const t = useTranslations('Feed');
  const [pickerOpen, setPickerOpen] = useState(false);
  // Opened from the keyboard: focus moves into the picker and back to the
  // button on close. Hover and long-press leave focus where it was.
  const openedFromKeyboard = useRef(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Swallows the click that follows the pointerup ending a long-press.
  const longPressFired = useRef(false);
  useEffect(
    () => () => {
      if (closeTimeout.current !== null) clearTimeout(closeTimeout.current);
      if (longPressTimeout.current !== null) clearTimeout(longPressTimeout.current);
    },
    [],
  );

  const openPicker = () => {
    if (closeTimeout.current !== null) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setPickerOpen(true);
  };
  const scheduleClose = () => {
    closeTimeout.current = setTimeout(() => setPickerOpen(false), PICKER_CLOSE_DELAY_MS);
  };
  const onPointerDown = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (e.pointerType !== 'touch') return;
    longPressTimeout.current = setTimeout(() => {
      longPressFired.current = true;
      openPicker();
    }, LONG_PRESS_MS);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (pickerOpen || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    openedFromKeyboard.current = true;
    openPicker();
  };
  const cancelLongPress = () => {
    if (longPressTimeout.current !== null) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  };

  const held = mine === null ? null : REACTION_BY_ID[mine];
  const label = held !== null ? t(REACTION_LABEL_KEY[held.id]) : t('reactions.like');
  // Each name contains the visible label (WCAG 2.5.3): "Like this post", or
  // "Remove your Love reaction" — a click on a held reaction removes it.
  const accessibleName =
    held !== null
      ? t('reactions.removeYours', { reaction: label })
      : t(`reactions.likeTarget.${target}`);
  const discSize = variant === 'chip' ? 22 : 18;

  const button = (
    <Button
      // Borderless in both variants, like the Comment action beside it; a
      // held reaction shows only in its own color.
      variant="ghost"
      size="sm"
      // Until the viewer reacts: the same outline-icon treatment as the
      // Comment action beside it. After: the held reaction's colored disc,
      // in the same leading slot so the label keeps its baseline.
      {...(held === null
        ? { icon: OUTLINE_HEART }
        : { icon: <ReactionDisc reaction={held} size={discSize} />, iconSize: discSize })}
      // The chip matches the post row's Comment action; the meta row's text
      // actions sit tight, since the sm tier's padding reads as gaps between
      // Like · Reply · time.
      padX={variant === 'chip' ? 'sm' : 'xs'}
      {...(flush !== undefined ? { flush } : {})}
      style={{
        ...(variant === 'text' ? { height: 'auto' } : {}),
        ...(held !== null ? { color: held.ink } : {}),
      }}
      aria-label={accessibleName}
      aria-pressed={held !== null}
      aria-keyshortcuts={disabled ? undefined : 'ArrowUp'}
      disabled={disabled}
      onClick={() => {
        if (longPressFired.current) {
          longPressFired.current = false;
          return;
        }
        setPickerOpen(false);
        onReact(held?.id ?? DEFAULT_REACTION);
      }}
    >
      {label}
    </Button>
  );

  if (disabled) return button;

  return (
    // Long-press and the keyboard shortcut listen on the wrapper in the
    // capture phase: Button routes its props through react-aria, whose press
    // handling neither forwards raw pointer handlers nor lets them bubble.
    // The Button itself is the popover trigger, so it carries
    // aria-haspopup / aria-expanded.
    <span
      onMouseEnter={openPicker}
      onMouseLeave={scheduleClose}
      onPointerDownCapture={onPointerDown}
      onPointerUpCapture={cancelLongPress}
      onPointerCancelCapture={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onKeyDownCapture={onKeyDown}
    >
      <Popover
        open={pickerOpen}
        // The trigger's own click is the toggle above, never an open; only
        // closes (Escape, an outside click, a pick) come through here.
        onOpenChange={(open) => {
          if (!open) setPickerOpen(false);
        }}
        allowOverflow
        onOpenAutoFocus={(e) => {
          if (!openedFromKeyboard.current) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          if (!openedFromKeyboard.current) e.preventDefault();
          openedFromKeyboard.current = false;
        }}
        trigger={button}
        side="top"
        align="start"
      >
        <div onMouseEnter={openPicker} onMouseLeave={scheduleClose}>
          <ReactionPicker
            mine={held?.id ?? null}
            onPick={(kind) => {
              onReact(kind);
              setPickerOpen(false);
            }}
          />
        </div>
      </Popover>
    </span>
  );
}
